/**
 * Browser-side cache for the meetings list.
 *
 * The list is the landing view of the Meet tab, and until this existed every
 * visit painted a spinner while a heavyweight request went to the meeting
 * service. The list barely changes between visits, so we keep the last good
 * response and hand it to React Query as `initialData` — the page paints
 * instantly from disk, then revalidates in the background.
 *
 * Three rules this file exists to enforce:
 *
 * 1. **Scoped to one user.** `logout()` clears the session token, not
 *    arbitrary localStorage, so an unscoped cache would show the previous
 *    person's meetings to whoever signs in next on a shared machine. The key
 *    carries the user id and a read for a different user is a miss, not a
 *    fallback. `clearAllCachedMeetings()` is called on sign-out as well, so
 *    the rows do not simply sit there.
 * 2. **Never throws.** localStorage throws outright in Safari private mode and
 *    under blocked-cookie policies, and is full more often than people expect.
 *    Every entry point is wrapped; a cache failure must degrade to "no cache",
 *    never to a broken page.
 * 3. **Bounded and expiring.** A stale list shown as though it were live is
 *    worse than a spinner, and localStorage is a ~5 MB budget shared with the
 *    rest of the app.
 */
import type { MeetingRecord } from "../types";

const PREFIX = "ubik-meetings-cache";
/** Bump when MeetingRecord changes shape, so old entries are ignored. */
const VERSION = "v1";
/** Older than this and we prefer a spinner over a lie. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Refuse to write more than this; localStorage is a shared ~5 MB budget. */
const MAX_BYTES = 1_500_000;

interface CacheEnvelope {
  version: string;
  userId: string;
  updatedAt: number;
  data: MeetingRecord[];
}

const keyFor = (userId: string) => `${PREFIX}:${VERSION}:${userId}`;

export interface CachedMeetings {
  data: MeetingRecord[];
  updatedAt: number;
}

/**
 * Last good list for this user, or null when there is nothing usable.
 * A miss is the normal case, not an error.
 */
export function readCachedMeetings(userId: string | null | undefined): CachedMeetings | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEnvelope;
    if (
      parsed?.version !== VERSION ||
      parsed.userId !== userId ||
      !Array.isArray(parsed.data) ||
      typeof parsed.updatedAt !== "number"
    ) {
      return null;
    }
    if (Date.now() - parsed.updatedAt > MAX_AGE_MS) {
      // Expired. Drop it so it stops occupying the budget.
      try {
        localStorage.removeItem(keyFor(userId));
      } catch {
        /* nothing useful to do */
      }
      return null;
    }
    return { data: parsed.data, updatedAt: parsed.updatedAt };
  } catch {
    // Corrupt JSON, blocked storage, or a shape from a previous release.
    return null;
  }
}

export function writeCachedMeetings(
  userId: string | null | undefined,
  data: MeetingRecord[],
): void {
  if (!userId || !Array.isArray(data)) return;
  try {
    const envelope: CacheEnvelope = {
      version: VERSION,
      userId,
      updatedAt: Date.now(),
      data,
    };
    const serialized = JSON.stringify(envelope);
    if (serialized.length > MAX_BYTES) {
      // A very long history would crowd out everything else. Drop any previous
      // entry rather than leaving a stale one behind pretending to be current.
      try {
        localStorage.removeItem(keyFor(userId));
      } catch {
        /* nothing useful to do */
      }
      return;
    }
    localStorage.setItem(keyFor(userId), serialized);
  } catch {
    // Quota exceeded or storage blocked — the page works fine without a cache.
  }
}

/**
 * Remove every user's cached list. Called on sign-out: the signing-out user's
 * rows must not be readable by the next person on this browser, and we cannot
 * know which id to target once the session is already torn down.
 */
export function clearAllCachedMeetings(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${PREFIX}:`)) doomed.push(key);
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage blocked — nothing was written either */
  }
}
